import {
	IsBoolean,
	IsEmail,
	IsObject,
	IsOptional,
	IsString,
	Validate,
} from 'class-validator';
import type { EmailEventEntity, EmailRecordEntity } from '../entity';
import { EmailMessage } from '../interfaces';
import { IsEmailAddressDtoOrString } from './send-email.dto';

// ============================================================================
// Provider Management DTOs
// ============================================================================

export interface EmailProviderDto {
	type: string;
	name: string;
	description: string;
	icon: string;
	features: Array<string>;
	authRequired: boolean;
}

export type GetProvidersResponseDto = Array<EmailProviderDto>;

export interface GetAuthUrlResponseDto {
	url: string;
	state: string;
}

export interface ConnectionSummaryDto {
	id: number;
	provider: string;
	email: string;
}

export interface AuthenticateSuccessResponseDto {
	success: true;
	connection: ConnectionSummaryDto;
}

export interface AuthenticateErrorResponseDto {
	success: false;
	message: string;
}

export interface AuthenticateResponseDto {
	success: boolean;
	connection?: ConnectionSummaryDto;
	message?: string;
}

export interface DisconnectProviderResponseDto {
	message: string;
}

export interface UserConnectionDto {
	id: number;
	provider: string;
	email: string;
	isActive: boolean;
	hasAuth: boolean;
	autoSyncEnabled: boolean;
	connectedAt: Date;
	lastUpdated: Date;
	signature?: string;
	signatureAutoAppend: boolean;
}

export interface GetUserConnectionsResponseDto {
	connections: Array<UserConnectionDto>;
}

// Request body for updating a connection's email signature settings.
export interface UpdateEmailSignatureRequestDto {
	signature: string | null;
	signatureAutoAppend: boolean;
}

// ============================================================================
// Sync Operations DTOs
// ============================================================================

export interface SyncConnectionResponseDto {
	message: string;
	jobId?: number;
}

export interface FullSyncConnectionResponseDto {
	message: string;
	fullSyncFromDate: string;
}

export interface CurrentSyncJobDto {
	id: number;
	status: string;
	type: string;
	totalFolders: number;
	completedFolders: number;
	totalMessages: number;
	completedMessages: number;
}

export interface GetSyncStatusResponseDto {
	status: string;
	lastSync?: Date;
	currentJob?: CurrentSyncJobDto;
}

export interface SyncFoldersResponseDto {
	message: string;
	emailSourceId: number;
}

export interface SyncFolderResponseDto {
	message: string;
}

// ============================================================================
// Subscription DTOs
// ============================================================================

export interface SubscriptionDto {
	id: number;
	resource: string;
	expirationDateTime: Date;
	notificationUrl: string;
	isActive: boolean;
	createdDate: Date;
	modifiedDate: Date;
}

export interface GetSubscriptionsResponseDto {
	subscriptions: Array<SubscriptionDto>;
}

export interface SubscriptionDiagnosticDto {
	id: number;
	resource: string;
	expirationDateTime: Date;
	isActive: boolean;
	remainingHours: number;
}

export interface GetSubscriptionDiagnosticsResponseDto {
	emailSourceId: number;
	connectionId: number;
	webhookUrl: string;
	autoSyncEnabled: boolean;
	hasActiveSubscriptions: boolean;
	subscriptionCount: number;
	subscriptions: Array<SubscriptionDiagnosticDto>;
	lastSync: Date | undefined;
	lastEvent: Date | undefined;
}

export interface DeleteSubscriptionResponseDto {
	message: string;
}

// ============================================================================
// Folder DTOs
// ============================================================================

export interface FolderDto {
	id: string;
	name: string;
	type: string;
	unreadCount: number;
	totalCount: number;
	parentId: string | null;
	children: Array<FolderDto>;
}

export type GetFoldersResponseDto = Array<FolderDto>;

// ============================================================================
// Message DTOs
// ============================================================================

// Not exported: send-email.dto.ts already exports a class-validator class
// with this exact name, and a second export would make the barrel ambiguous.
interface EmailAddressDto {
	email: string;
	name?: string | undefined;
}

export interface MessageAttachmentDto {
	id: string;
	name: string;
	size: number | undefined;
	contentType: string;
}

export interface MessageDto {
	id: string;
	conversationId: string | null;
	subject: string;
	preview: string;
	from: EmailAddressDto;
	to: Array<EmailAddressDto>;
	cc: Array<EmailAddressDto>;
	bcc: Array<EmailAddressDto>;
	date: Date;
	isRead: boolean;
	isStarred: boolean;
	isDraft: boolean;
	hasAttachments: boolean;
	attachments: Array<MessageAttachmentDto>;
}

export interface GetMessagesResponseDto {
	items: Array<{
		id: string;
		conversationId: string;
		subject: string;
		preview: string;
		from: {
			email: string;
			name: string;
		};
		date: Date;
		isRead: boolean;
		isStarred: boolean;
		hasAttachments: boolean;
		folderId?: string;
		folderName?: string;
	}>;
	total: number;
	page: number;
	pageSize: number;
}

export interface GetMessageResponseDto {
	id: string;
	conversationId: string | undefined;
	subject: string;
	preview: string;
	from: EmailAddressDto;
	to: Array<EmailAddressDto>;
	cc: Array<EmailAddressDto>;
	bcc: Array<EmailAddressDto>;
	date: Date | undefined;
	isRead: boolean;
	isStarred: boolean;
	isDraft: boolean;
	hasAttachments: boolean;
	attachments: Array<MessageAttachmentDto>;
	body: string;
	folderId: string;
	/** Email source ID for shared email tracking */
	emailSourceId: number | null;
	/** User ID of the email source owner (for share/hide toggle permissions) */
	sourceOwnerId: number | null;
}

export interface MarkAsStarredResponseDto {
	success: boolean;
}

export interface MarkAsReadResponseDto {
	success: boolean;
}

/**
 * Request body for `PUT /email/records/read` — apply one read state to many
 * email records in a single round trip.
 *
 * Deliberately an interface, not a class: class-based `@Body()` DTOs have to
 * be value-imported by the controller or the global ValidationPipe strips the
 * whole body (see the import note in email.controller.ts).
 */
export interface MarkRecordsReadRequestDto {
	/** `email_record` ids. Duplicates are collapsed server-side. */
	emailIds: Array<string>;
	isRead: boolean;
}

export interface MarkRecordsReadResponseDto {
	success: boolean;
	/** Ids the read state was actually applied to. */
	updated: Array<string>;
	/**
	 * Ids the caller could not act on, with the reason. Partial success is
	 * reported rather than failing the batch: one inaccessible record in a
	 * multi-select shouldn't discard the rest of the work.
	 */
	skipped: Array<{ emailId: string; reason: string }>;
}

// ============================================================================
// Email Sending DTOs
// ============================================================================

/**
 * Request body for sending or drafting an email through a connected mailbox
 * (POST /email/accounts/:email/send and POST/PUT /email/accounts/:email/drafts).
 *
 * Attachments are NOT uploaded inline with this request — the composer first
 * uploads each file via POST /files/upload (S3 presigned PUT) and passes the
 * resulting file ids here. The server resolves those ids back to file content
 * before handing the message to the provider.
 */
export interface SendEmailThroughAccountRequestDto {
	to: Array<EmailAddressDto>;
	cc?: Array<EmailAddressDto>;
	bcc?: Array<EmailAddressDto>;
	subject: string;
	body: {
		html?: string;
		text?: string;
	};
	/** Ids of previously-uploaded files (file table) to attach. */
	fileIds?: Array<number>;
	/** Id of the message being replied to (threading). */
	inReplyTo?: string;
}

export interface SendEmailThroughAccountResponseDto {
	messageId: string | null;
	status: 'sent' | 'queued' | 'failed';
	provider: string;
	timestamp: Date;
	error?: string;
}

// ============================================================================
// Auto-Sync DTOs
// ============================================================================

export interface ToggleAutoSyncResponseDto {
	success: boolean;
	autoSyncEnabled: boolean;
}

// ============================================================================
// Email History DTOs
// ============================================================================

// Import EmailRecordEntity type
export type GetEmailResponseDto =
	import('../entity/email-record.entity').EmailRecordEntity;

// ============================================================================
// Template Builder DTOs
// ============================================================================

export interface MjmlErrorDto {
	line: number;
	message: string;
	tagName: string;
	formattedMessage: string;
}

export interface CompileTemplateResponseDto {
	html: string;
	errors: Array<MjmlErrorDto>;
}

export interface ValidateTemplateResponseDto {
	isValid: boolean;
	errors: Array<MjmlErrorDto>;
}

export interface PreviewTemplateResponseDto {
	html: string;
	errors: Array<MjmlErrorDto>;
}

export interface StructureToMjmlResponseDto {
	mjml: string;
}

// ============================================================================
// System Sent Emails DTOs
// ============================================================================

export interface SystemSentEmailDto {
	id: string;
	messageId: string | null;
	subject: string;
	fromEmail: string | null;
	fromName: string | null;
	recipientsDisplay: string;
	sendStatus: string;
	sentAt: Date | null;
	date: Date;
	errorMessage: string | null;
	sourceProvider: string | null;
	emailTemplateId: number | null;
	automationStepExecutionId: number | null;
}

export interface GetSystemSentEmailsResponseDto {
	success: boolean;
	data?: Array<SystemSentEmailDto>;
	pagination?: {
		total: number;
		totalPages: number;
		page: number;
		limit: number;
	};
	total?: number;
}

export interface GetSystemSentEmailsBatchCountResponseDto {
	success: boolean;
	total: number;
}

export interface EmailTemplateReferenceDto {
	id: number;
	name: string;
}

export interface AutomationStepExecutionReferenceDto {
	id: number;
	executionId: number;
}

export interface SystemSentEmailDetailsDto
	extends Omit<EmailRecordEntity, 'automationStepExecution'> {
	recipientsDisplay: string;
	eventsCount: number;
	emailTemplate?: EmailTemplateReferenceDto;
	automationStepExecution?: AutomationStepExecutionReferenceDto;
}

export interface GetSystemSentEmailByIdResponseDto {
	success: boolean;
	data: SystemSentEmailDetailsDto;
}

export interface GetEmailEventsResponseDto {
	success: boolean;
	data: Array<EmailEventEntity>;
}

export interface GetSystemSentEmailAttachmentResponseDto {
	success: boolean;
	url: string;
	name: string;
	mimeType: string;
	size: number | undefined;
}

// ============================================================================
// Webhook DTOs (Note: These return unknown as they delegate to provider logic)
// ============================================================================

export type HandleWebhookResponseDto = unknown;
export type HandleEventWebhookResponseDto = unknown;
export type RefreshSubscriptionsResponseDto = unknown;

export class EmailEntityDto<
	TTemplateDataInterface extends
		EmailMessage['templateData'] = EmailMessage['templateData'],
> {
	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	public to!: EmailAddressDto | EmailAddressDto[] | string | string[];

	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	@IsOptional()
	public cc?: EmailAddressDto | EmailAddressDto[] | string | string[];

	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	@IsOptional()
	public bcc?: EmailAddressDto | EmailAddressDto[] | string | string[];

	@IsString()
	@IsOptional()
	public subject?: string;

	/**
	 * Optional from-address (the "Send From" domain selector). Only honored
	 * when the domain is tenant-owned and verified at the active email
	 * provider — EmailService.sendTenantEmail rewrites anything else to the
	 * platform fallback address. Omitted = tenant's primary domain.
	 */
	@IsEmail()
	@IsOptional()
	public from?: string;

	@IsBoolean()
	@IsOptional()
	public attachPdf?: boolean;

	@IsObject()
	@IsOptional()
	public templateData?: Partial<TTemplateDataInterface>;
}
